import api from './api';

// ── Types ────────────────────────────────────────────────────────────────────
export interface HypeExample {
  title: string;
  url: string | null;
  author?: string | null;
  metrics: Record<string, number>;
  hype_score: number;
  created_at: string | null;
}

export interface TrendTerm {
  term: string;
  type: 'hashtag' | 'keyword';
  score: number;
  count: number;
  delta?: number;
  category?: string;
}

export interface HypeCreator {
  name: string;
  velocity: number;
  views: number;
  count: number;
  category?: string;
}

export interface SeriesPoint {
  t: string;
  value: number;
}

export interface HypeTrend {
  trend_id: string;
  label: string;
  keywords: string[];
  hype_score: number;
  momentum: 'surging' | 'rising' | 'steady' | 'cooling';
  change_24h: number;
  series: SeriesPoint[];
  terms: TrendTerm[];
  total_views: number;
  delta_score: number | null;
  stats: { creators?: HypeCreator[]; format?: { short_pct: number; long_pct: number }; peak_hours?: number[] };
  signal_count: number;
  examples: HypeExample[];
}

export interface LeaderboardItem {
  title: string;
  url: string | null;
  author: string | null;
  views: number;
  velocity: number;
  engagement: number;
  category: string;
}

export interface HypeOverview {
  kpis: {
    trend_count?: number;
    video_count?: number;
    total_views?: number;
    avg_freshness?: number;
    top_category?: string;
  };
  leaderboard: LeaderboardItem[];
  top_terms: TrendTerm[];
  creators: HypeCreator[];
  format_mix: { short_pct?: number; long_pct?: number };
  best_hours: number[];
}

export interface HypeTrendsResponse {
  trends: HypeTrend[];
  total: number;
  overview: HypeOverview;
  generated_at: string;
  config: { country: string; city: string | null; categories: string[]; keywords: string[] };
}

export interface HypeConfig {
  id: string;
  country: string;
  city: string | null;
  categories: string[];
  keywords: string[];
  languages: string[];
  handles: string[];
  enabled: boolean;
}

export type HypeConfigUpdate = Partial<Omit<HypeConfig, 'id'>>;

// ── API ──────────────────────────────────────────────────────────────────────
export async function getHypeConfig(): Promise<HypeConfig> {
  const { data } = await api.get('/hype/config');
  return data;
}

export async function updateHypeConfig(update: HypeConfigUpdate): Promise<HypeConfig> {
  const { data } = await api.put('/hype/config', update);
  return data;
}

export async function getHypeCategories(): Promise<Record<string, string[]>> {
  const { data } = await api.get('/hype/categories');
  return data.groups;
}

export async function getHypeTrends(refresh = false): Promise<HypeTrendsResponse> {
  const { data } = await api.get('/hype/trends', { params: { refresh } });
  return data;
}

// ── Advice (content brief + ROI) ──────────────────────────────────────────────
export interface ContentIdea {
  format: string;
  hook: string;
  angle: string;
  example_caption: string;
}

export interface HypeRecommendation {
  summary: string;
  why_now: string;
  best_format: string;
  content_ideas: ContentIdea[];
  hashtags: string[];
  posting_tips: string;
  engagement_tier: 'high' | 'medium' | 'low';
}

export interface RoiScenario {
  reach: number;
  leads: number;
  customers: number;
  revenue: number;
  roi_pct: number;
}

export interface HypeRoi {
  scenarios: Record<'conservative' | 'expected' | 'optimistic', RoiScenario>;
  reach_multiplier: number;
  assumptions: Record<string, number>;
  disclaimer: string;
}

export interface HypeAdvice {
  trend: HypeTrend;
  recommendation: HypeRecommendation;
  roi: HypeRoi;
}

export async function getTrendAdvice(trendId: string): Promise<HypeAdvice> {
  const { data } = await api.get(`/hype/trends/${trendId}/advice`);
  return data;
}
